import React, { useEffect } from 'react';
import { TextField, InputAdornment, IconButton } from '@mui/material/';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';

interface Props {
  search?: string;
  setGlobalFilter?: (filter: string) => void;
  placeholder?: string;
  disabled?: boolean;
}

const Search: React.FC<Props> = ({
  search = '',
  setGlobalFilter,
  placeholder,
  disabled,
}) => {
  const [value, setValue] = React.useState(search);
  const didMountRef = React.useRef(false);
  const timeout = React.useRef<ReturnType<typeof setTimeout>>();

  const handleChange = React.useCallback(
    (val) => {
      if (timeout.current) {
        clearTimeout(timeout.current);
      }
      if (!setGlobalFilter) return;
      timeout.current = setTimeout(() => {
        setGlobalFilter(val || '');
      }, 500);
    },
    [setGlobalFilter],
  );

  useEffect(() => setValue(search), [search]);

  useEffect(() => {
    if (didMountRef.current && (!value || value.length >= 2)) {
      handleChange(value);
    } else {
      didMountRef.current = true;
    }
  }, [value]);

  return (
    <TextField
      fullWidth
      data-testid="search-box"
      type="search"
      variant="outlined"
      disabled={disabled}
      value={value || ''}
      onChange={(e) => { setValue(e.target.value); }}
      sx={{
        backgroundColor: (theme) => theme.palette.common.white,
        '& input::-webkit-search-decoration, & input::-webkit-search-cancel-button, & input::-webkit-search-results-button, & input::-webkit-search-results-decoration': {
          display: 'none',
        },
      }}
      placeholder={placeholder ?? 'Search'}
      InputLabelProps={{ shrink: true }}
      InputProps={{
        endAdornment: (
          <InputAdornment position="end">
            {value && (
              <IconButton
                aria-label="clear search"
                onClick={() => setValue('')}
                edge="end"
                size="small"
                sx={{
                  color: 'primary.main',
                  marginRight: 1,
                }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            )}
            <SearchIcon />
          </InputAdornment>
        ),
      }}
      size="small"
      onKeyPress={(e) => e.key === 'Enter' && e.preventDefault()}
    />
  );
};

export default React.memo(Search);
